import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Input, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { AssessmentApplicationService } from '@app/core';
import {
  AssessmentAnnulTypeEnum,
  AssessmentApplicationMode,
  AssessmentLevelContent,
  AssessmentManagedType,
  AssessmentWeightType,
  Entidade,
  FunctionsEnum,
} from '@app/core/models';
import { ReportApiRoutesEnum } from '@app/core/models/report-api-routes.enum';
import { notNull, safeEmpty, selectedEntityActive } from '@app/shared';
import { Popover, Strategy } from '@app/shared/modules/template/components/popover';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { compact, first, isEmpty, isEqual, merge, uniq } from 'lodash';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs';
import { PopoverAnnulQuestionComponent } from '../popover-annul-question/popover-annul-question.component';

@Component({
  selector: 'app-tab-questions-map',
  templateUrl: './tab-questions-map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabQuestionsMapComponent implements OnDestroy {
  public readonly assessmentWeightType = AssessmentWeightType;
  public readonly strategy = Strategy;
  public readonly levelContent = AssessmentLevelContent;
  public modeloCtrl = new FormControl();
  public data$ = new BehaviorSubject<any>(undefined);
  public loading$ = new BehaviorSubject<boolean>(false);
  public application$ = new BehaviorSubject<any>(undefined);
  private _modeloId: number;
  private _activeUser$ = new BehaviorSubject<any>(undefined);
  private _isCoAuthor = false;
  private _defaultFunction: number = 0;
  private _entityActive: Entidade;

  constructor(
    private _evaluationService: AssessmentApplicationService,
    private _popover: Popover,
    private destroy: DestroyRef,
    private _store: Store<AppState>,
    private _destroyRef: DestroyRef,
  ) {
    this._store
      .select(AppSelectors.DefaultFunction)
      .pipe(takeUntilDestroyed(this._destroyRef), notNull(), distinctUntilChanged(isEqual))
      .subscribe((defaultFunction) => (this._defaultFunction = defaultFunction));

    const activeUser$ = this._store.select(AppSelectors.ActiveUser).pipe(takeUntilDestroyed(this.destroy), notNull());
    const application$ = this.application$.pipe(takeUntilDestroyed(this.destroy), notNull());
    combineLatest({
      application: application$,
      activeUser: activeUser$,
      modelo: this.modeloCtrl.valueChanges.pipe(startWith(undefined)),
    })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(({ application, activeUser }) => {
          this._activeUser$.next(activeUser);
          this._entityActive = first(selectedEntityActive(activeUser));
          this.loading$.next(true);
          this.data$.next(undefined);
          this._modeloId = activeUser.pais_id;
          if (
            application.coautores?.some((coautor) => coautor.user_id === activeUser.usuario_id) &&
            activeUser.usuario_id !== application.avaliacao.criado_por
          ) {
            this._isCoAuthor = true;
          }
        }),
        switchMap(({ application, modelo }) =>
          this._evaluationService.evaluationMap(application.uuid, modelo).pipe(safeEmpty()),
        ),
        map((data: any) => data.avaliacao.avaliacao_questoes),
      )
      .subscribe((data) => {
        this.data$.next(data);
        this.loading$.next(false);
      });
  }

  public isEmpty(items: any): boolean {
    return isEmpty(items);
  }

  public get isPAAEB(): boolean {
    return (
      this.application$.value?.avaliacao?.tipo_gerenciada === AssessmentManagedType.PAAEB ||
      this.application$.value?.avaliacao?.tipo_gerenciada === AssessmentManagedType.PAEE
    );
  }

  public get isDivision(): boolean {
    return this._entityActive.tipo === 5;
  }

  public get canSelectModelo(): boolean {
    return (
      this.application$.value.modo_aplicacao !== AssessmentApplicationMode.Online &&
      (!!this.application$.value.embaralhar_questoes || !!this.application$.value.embaralhar_alternativas)
    );
  }

  @Input()
  public set application(value) {
    this.application$.next(value);
  }

  public ngOnDestroy(): void {
    this.data$.complete();
    this.application$.complete();
    this.loading$.complete();
  }

  public toLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  public onUpdateQuestionAnula(data: any, question: any): void {
    const currentDataQuest = this.data$.getValue();
    const updatedQuestoes = currentDataQuest.map((q: any) => {
      if (q.questao.uuid === question.questao.uuid) {
        return {
          ...q,
          anulado: data.anulado,
          anulado_motivo: data.anulado_motivo,
        };
      }
      return q;
    });
    this.data$.next(updatedQuestoes);
  }

  public onOpenPopover(elementRef: ElementRef, questao: any): void {
    this._popover
      .open(PopoverAnnulQuestionComponent, {
        positionStrategy: [this.strategy.LEFT],
        elementRef,
        data: {
          aplicacao_uuid: this.application$.value.uuid,
          question: questao.questao,
          type: AssessmentAnnulTypeEnum.Question,
        },
      })
      .closed.pipe(notNull())
      .subscribe((res: any) => {
        this.onUpdateQuestionAnula(res, questao);
      });
  }

  public onExport() {
    const data = {
      api_route: ReportApiRoutesEnum.ReportMap,
      modelo: this.modeloCtrl.value,
    };

    this._evaluationService.reportExport(this.application$.value.uuid, data, 'mapa-avaliacao');
  }

  public onExportAll() {
    const data = {
      api_route: ReportApiRoutesEnum.ReportMapAll,
    };

    this._evaluationService.reportExportAll(this.application$.value.uuid, data, 'mapa-avaliacao-todos-modelos');
  }

  public getComponentNames(categories: any[], summaries: []): string[] {
    const categoriesDisciplines = categories.map((category) => category.component?.name);
    const summaryDisciplines = summaries.map(
      (summary: any) =>
        summary.livro?.contextos?.find((context) => context.modelo_id === this._modeloId)?.padrao_disciplina?.descricao,
    );

    return uniq(compact(merge(categoriesDisciplines, summaryDisciplines)));
  }

  public canAnular(): boolean {
    return (
      !this._isCoAuthor &&
      (this.application$.value.avaliacao.criado_por === this._activeUser$.value.usuario_id ||
        this.permissionView(this.application$.value.avaliacao) ||
        [FunctionsEnum.desenvolvedorPortal].includes(this._defaultFunction))
    );
  }

  public sliceStatement(statement: string) {
    if (!statement) {
      return '';
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = statement;

    const plainText = tempDiv.textContent?.replace(/\s+/g, ' ').trim() || '';

    const imgElement = tempDiv.querySelector('img');

    if (plainText.length > 0) {
      return plainText.slice(0, 40) + (plainText.length > 40 ? '...' : '');
    }

    if (imgElement) {
      imgElement.classList.add('w-24');
      imgElement.style.height = 'auto';
      return imgElement.outerHTML;
    }

    return '';
  }

  private permissionView(assessment): boolean {
    const options = {
      [1]: () => 'entidade_id',
      [2]: () => 'campo_id',
      [3]: () => 'uniao_id',
      [4]: () => 'editora_id',
      [5]: () => 'divisao_id',
    };
    const existePermissao = assessment.entidade_permissao.some(
      (permissao) => permissao[options[this._entityActive.tipo]()] === this._entityActive.id,
    );
    return existePermissao;
  }
}
